/** A circle with two letters in it.
 *
 * Provider avatars are available — the identities carry a URL — but the
 * initials are the default, because a row of GitHub photographs would be the
 * loudest thing on a page whose whole design is restraint. Where a picture is
 * wanted, pass `src`; it falls back to the initials if the image does not
 * load, which it will not for a deleted account.
 *
 * The circle is decorative: the username is always written beside it in the
 * design, so repeating it to a screen reader would just be an echo.
 */

import { useState } from 'react';
import { initials } from '@/lib/format';

export interface AvatarProps {
  /** The display name the initials come from. */
  name: string;
  size?: number;
  /** A provider avatar, when a screen wants the real face. */
  src?: string | null;
  className?: string;
}

export function Avatar({ name, size = 24, src, className }: AvatarProps) {
  const [broken, setBroken] = useState(false);

  const common = {
    width: size,
    height: size,
    borderRadius: '50%',
    flex: 'none' as const,
  };

  if (src && !broken) {
    return (
      <img
        src={src}
        alt=""
        aria-hidden="true"
        className={className}
        onError={() => setBroken(true)}
        style={{ ...common, objectFit: 'cover', background: 'var(--color-accent-800)' }}
      />
    );
  }

  return (
    <span
      aria-hidden="true"
      className={className}
      style={{
        ...common,
        display: 'grid',
        placeItems: 'center',
        background: 'var(--color-accent-800)',
        color: 'var(--color-accent-200)',
        // The design's pairs: 24px circle / 10px letters, 40 / 14, 52 / 18.
        fontSize: Math.max(9, Math.round(size * 0.36)),
        lineHeight: 1,
        letterSpacing: '0.02em',
        userSelect: 'none',
      }}
    >
      {initials(name)}
    </span>
  );
}

export default Avatar;
